const DEFAULT_WS_URL = 'wss://app.biel.ai/ws/messages';

export class BielWebSocketService {
  private ws: WebSocket;
  private messageHandlers: ((message: any) => void)[] = [];
  private isConnected: boolean = false;
  private projectId: string;
  private chatId: string = '';
  private email: string = '';
  private apiKey: string = '';
  private errorMessage: string;
  private loadingStartHandler: () => void;
  private loadingEndHandler: () => void;
  private connectionStatusHandlers: ((isConnected: boolean) => void)[] = [];
  private wsUrl: string;
  private thinkMode: boolean = false;
  private metadata: string = '';

  // Auto-reconnect state. A dropped socket (server restart, a network blip, a
  // proxy that killed an idle connection) used to leave `isConnected` false
  // forever, which disables the input until the user reloads. Instead we retry
  // with exponential backoff. `isClosing` suppresses that retry when the close
  // was intentional (closeSocket, e.g. the refresh button building a new
  // socket), and the attempt counter caps how long we keep trying so a socket
  // the server keeps rejecting doesn't reconnect in a tight loop forever.
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 6;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isClosing: boolean = false;

  constructor(
    projectId: string,
    errorMessage: string,
    email: string,
    apiKey: string = '',
    baseUrl: string = '',
    initialChatId: string = '',
    thinkMode: boolean = false,
    metadata: string = ''
  ) {
    this.projectId = projectId;
    this.errorMessage = errorMessage;
    this.email = email;
    this.apiKey = apiKey;
    this.chatId = initialChatId;
    this.thinkMode = thinkMode;
    this.metadata = metadata;

    // Convert HTTP/HTTPS base URL to WebSocket URL
    if (baseUrl) {
      const httpUrl = baseUrl.replace(/\/api\/v[12]\/?$/, '').replace(/\/+$/, ''); // Remove API path
      this.wsUrl =
        httpUrl.replace('http://', 'ws://').replace('https://', 'wss://') + '/ws/messages';
    } else {
      this.wsUrl = DEFAULT_WS_URL;
    }

    this.connect();
  }

  // Detach the current socket's handlers and close it. A reconnect replaces
  // this.ws; without this, a late event (a delayed close from the socket that
  // just dropped) would still run against shared state and could mark the
  // healthy replacement disconnected or kick off another retry.
  private teardownSocket(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
    }
  }

  private connect(): void {
    // Dispose the previous socket before opening a new one, so its stale
    // callbacks can't fire against the replacement.
    this.teardownSocket();

    let wsUrl: string;

    if (this.chatId) {
      // Use specific chat route for existing conversations
      wsUrl = `${this.wsUrl}/chat/${this.chatId}/`;
    } else {
      // Use project route for new conversations
      wsUrl = `${this.wsUrl}/${this.projectId}/`;
    }

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      // A clean open means the endpoint is healthy again; reset the backoff so
      // a later drop starts retrying promptly rather than at the capped delay.
      this.reconnectAttempts = 0;
      this.isConnected = true;
      this.connectionStatusHandlers.forEach((handler) => handler(true));
    };

    this.ws.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (!this.chatId && message.chatId) {
        this.chatId = message.chatId;
      }

      if (message.from) {
        // Always include the current chatId in the message
        const messageWithChatId = {
          ...message,
          chatId: this.chatId || message.chatId,
        };

        this.loadingEndHandler();
        this.messageHandlers.forEach((handler) => handler(messageWithChatId));
      }
    };

    this.ws.onclose = () => {
      this.isConnected = false;
      this.connectionStatusHandlers.forEach((handler) => handler(false));
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.isConnected = false;
      this.connectionStatusHandlers.forEach((handler) => handler(false));
      if (this.loadingEndHandler) {
        this.loadingEndHandler();
      }
      // A close normally follows an error and would schedule this too, but a
      // spec-noncompliant environment can fire error without close. Schedule
      // from both; scheduleReconnect is idempotent while a retry is pending, so
      // whichever fires first wins and the other is a no-op.
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    // Bail if the close was intentional (closeSocket), a retry is already
    // pending, or the server keeps dropping us past the cap. Handlers stay
    // registered across a reconnect, so when a later attempt opens, onopen
    // re-broadcasts connected=true and the input re-enables on its own.
    if (
      this.isClosing ||
      this.reconnectTimer ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      return;
    }
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  public sendMessage(message: string): void {
    const payload = {
      projectId: this.projectId,
      chatId: this.chatId,
      url: window.location.href,
      email: this.email,
      from: 'user',
      message: message,
      thinkMode: this.thinkMode,
    };

    // Add API key to payload if provided
    if (this.apiKey) {
      payload['apiKey'] = this.apiKey;
    }

    // Add metadata to payload if provided
    if (this.metadata) {
      payload['metadata'] = this.metadata;
    }

    if (this.isConnected) {
      this.ws.send(JSON.stringify(payload));
      this.loadingStartHandler();
    } else {
      this.messageHandlers.forEach((handler) =>
        handler({ message: this.errorMessage, from: 'ai' })
      );
    }
  }

  public closeSocket(): void {
    // Mark the close intentional and cancel any pending retry so the socket
    // stays down (the refresh path builds a fresh service after this).
    this.isClosing = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Detach every handler before closing: the server may still be streaming
    // a response over this socket, and any late chunk or close event from a
    // stale socket must not reach the component after a newer connection
    // (e.g. created by the refresh button) has taken over.
    this.messageHandlers = [];
    this.connectionStatusHandlers = [];
    this.loadingStartHandler = undefined;
    this.loadingEndHandler = undefined;
    this.teardownSocket();
    this.isConnected = false;
  }

  public onMessage(handler: (message: any) => void): void {
    this.messageHandlers.push(handler);
  }

  public onLoadingStart(handler: () => void): void {
    this.loadingStartHandler = handler;
  }

  public onLoadingEnd(handler: () => void): void {
    this.loadingEndHandler = handler;
  }

  public onConnectionStatusChange(handler: (isConnected: boolean) => void): void {
    this.connectionStatusHandlers.push(handler);
    handler(this.isConnected);
  }

  public getChatId(): string {
    return this.chatId;
  }

  public isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  public getConnectionInfo(): { isConnected: boolean; chatId: string; wsUrl: string } {
    return {
      isConnected: this.isConnected,
      chatId: this.chatId,
      wsUrl: this.ws?.url || '',
    };
  }

  public setThinkMode(enabled: boolean): void {
    this.thinkMode = enabled;
  }
}
